import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Heart, Trash2, ArrowRightLeft, Star } from "lucide-react";
import { toast } from "sonner";

const priorityConfig = {
  Low: { color: "text-zinc-400", bg: "bg-zinc-500/10", border: "border-zinc-500/20" },
  Medium: { color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  High: { color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  Grail: { color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20" },
};

export default function WishlistPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const fetchWishlist = useCallback(async () => {
    try {
      const { data } = await api.get("/wishlist");
      setEntries(data.entries);
    } catch { toast.error("Failed to load wishlist"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  const handleRemove = async (wishlistId) => {
    try {
      await api.delete(`/wishlist/${wishlistId}`);
      toast.success("Removed from wishlist");
      fetchWishlist();
    } catch { toast.error("Failed to remove"); }
  };

  const handleConvert = async (wishlistId) => {
    try {
      await api.post(`/wishlist/${wishlistId}/convert`);
      toast.success("Moved to collection!");
      fetchWishlist();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handlePriorityChange = async (wishlistId, priority) => {
    try {
      await api.put(`/wishlist/${wishlistId}`, { priority });
      fetchWishlist();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = filter === "all" ? entries : entries.filter((e) => e.priority === filter);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        {["wish-skel-1", "wish-skel-2", "wish-skel-3", "wish-skel-4"].map((id) => <div key={id} className="h-24 bg-zinc-900 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div data-testid="wishlist-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight">Wishlist</h1>
          <p className="text-zinc-500 mt-1 text-sm">{entries.length} items on your radar</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="wishlist-filter" className="w-[140px] bg-zinc-900 border-zinc-800 text-zinc-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="Grail">Grail</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <Heart className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-lg">Your wishlist is empty</p>
          <p className="text-zinc-600 text-sm mt-1">Browse the catalog and add items</p>
          <Button onClick={() => navigate("/catalog")} className="mt-4 bg-rose-600 hover:bg-rose-500 text-white">
            Browse Catalog
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((entry) => {
            const r = entry.release || {};
            const pc = priorityConfig[entry.priority] || priorityConfig.Medium;
            return (
              <div key={entry.wishlist_id} className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4 flex gap-4 items-center hover:border-zinc-700 transition-colors">
                {r.model_image ? (
                  <img
                    src={resolveImageUrl(r.model_image)}
                    alt={r.release_name}
                    className="w-20 h-20 rounded-lg object-cover cursor-pointer flex-shrink-0"
                    onClick={() => navigate(`/catalog/${entry.release_id}`)}
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-lg bg-zinc-800 flex flex-col items-center justify-center cursor-pointer flex-shrink-0"
                    onClick={() => navigate(`/catalog/${entry.release_id}`)}
                  >
                    <span className="text-[9px] text-zinc-600 font-mono">{r.khmg_number}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-zinc-100 truncate cursor-pointer hover:text-rose-400" onClick={() => navigate(`/catalog/${entry.release_id}`)}>
                      {r.release_name}
                    </p>
                    <span className={`${pc.bg} ${pc.color} border ${pc.border} px-2 py-0.5 rounded-full text-[10px] font-bold uppercase`}>
                      {entry.priority === "Grail" && <Star className="w-2.5 h-2.5 inline mr-0.5" />}{entry.priority}
                    </span>
                    {r.is_chase && <span className="bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full text-[10px] font-medium">CHASE</span>}
                    {r.is_blkltd && <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded-full text-[10px] font-medium">BLKLTD</span>}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{r.vehicle} &middot; {r.series}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs text-zinc-400">Value: <span className="font-mono text-rose-500">{r.estimated_market_value != null ? `$${r.estimated_market_value}` : 'TBD'}</span></span>
                    {entry.target_price && <span className="text-xs text-zinc-400">Target: <span className="font-mono text-emerald-400">${entry.target_price}</span></span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Select value={entry.priority} onValueChange={(v) => handlePriorityChange(entry.wishlist_id, v)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 text-xs h-8 w-24"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {["Low", "Medium", "High", "Grail"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-1">
                    <button
                      data-testid={`convert-${entry.wishlist_id}`}
                      onClick={() => handleConvert(entry.wishlist_id)}
                      className="flex-1 p-1.5 text-emerald-500 hover:bg-zinc-800 rounded transition-colors"
                      title="Move to Collection"
                    >
                      <ArrowRightLeft className="w-3.5 h-3.5 mx-auto" />
                    </button>
                    <button
                      data-testid={`remove-wish-${entry.wishlist_id}`}
                      onClick={() => handleRemove(entry.wishlist_id)}
                      className="flex-1 p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800 rounded transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5 mx-auto" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
